import { Component } from 'react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Only log in development
    if (process.env.NODE_ENV !== 'production') {
      console.error('ErrorBoundary caught:', error, info);
    }
  }

  render() {
    if (this.state.hasError) {
      return <MaintenancePage />;
    }
    return this.props.children;
  }
}

function MaintenancePage() {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f1117',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Manrope', 'Inter', sans-serif",
      padding: '24px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes wrenchSwing {
          0%   { transform: rotate(-20deg); }
          50%  { transform: rotate(20deg); }
          100% { transform: rotate(-20deg); }
        }
        @keyframes boltBounce {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          30%       { transform: translateY(-10px) rotate(15deg); }
          60%       { transform: translateY(-4px) rotate(-8deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(0.92); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes orbFloat {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-18px); }
        }
        .maint-card { animation: fadeUp 0.6s ease both; }
        .maint-card:nth-child(2) { animation-delay: 0.1s; }
        .maint-card:nth-child(3) { animation-delay: 0.2s; }
        .maint-card:nth-child(4) { animation-delay: 0.3s; }
        .maint-card:nth-child(5) { animation-delay: 0.4s; }
        .maint-card:nth-child(6) { animation-delay: 0.5s; }
        .try-again-btn:hover { transform: translateY(-2px) !important; box-shadow: 0 8px 28px rgba(245,158,11,0.55) !important; }
      `}</style>

      {/* Background orbs */}
      <div style={{
        position: 'absolute', width: '400px', height: '400px',
        background: 'radial-gradient(circle, rgba(245,158,11,0.08) 0%, transparent 70%)',
        borderRadius: '50%', top: '-80px', left: '-100px', pointerEvents: 'none',
        animation: 'orbFloat 7s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', width: '300px', height: '300px',
        background: 'radial-gradient(circle, rgba(239,68,68,0.06) 0%, transparent 70%)',
        borderRadius: '50%', bottom: '-60px', right: '-80px', pointerEvents: 'none',
        animation: 'orbFloat 9s ease-in-out infinite reverse',
      }} />

      {/* Wrench + Bolt illustration */}
      <div className="maint-card" style={{ position: 'relative', width: '130px', height: '130px', marginBottom: '32px' }}>
        {/* Wrench */}
        <svg
          width="90" height="90"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#f59e0b"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            position: 'absolute', top: '10px', left: '0',
            animation: 'wrenchSwing 2.4s ease-in-out infinite',
            transformOrigin: '18px 18px',
            filter: 'drop-shadow(0 0 12px rgba(245,158,11,0.5))',
          }}
        >
          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
        </svg>
        {/* Bolt */}
        <svg
          width="44" height="44"
          viewBox="0 0 24 24"
          fill="#f97316"
          stroke="#f97316"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            position: 'absolute', bottom: '0', right: '0',
            animation: 'boltBounce 2s ease-in-out infinite',
            filter: 'drop-shadow(0 0 8px rgba(249,115,22,0.6))',
          }}
        >
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
        {/* Glow ring */}
        <div style={{
          position: 'absolute', inset: 0,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245,158,11,0.1) 0%, transparent 70%)',
        }} />
      </div>

      {/* Logo badge */}
      <div className="maint-card" style={{
        width: '72px',
        height: '72px',
        borderRadius: '50%',
        backgroundImage: "url('https://customer-assets.emergentagent.com/job_e67a0971-45d9-4d0f-b06b-f0d31f74177c/artifacts/0mpqvdtb_IMG_5829.jpeg')",
        backgroundSize: '118%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        marginBottom: '20px',
        boxShadow: '0 6px 28px rgba(0,0,0,0.4)',
      }} />

      {/* Heading */}
      <h1 className="maint-card" style={{
        color: 'white',
        fontSize: '30px',
        fontWeight: '800',
        marginBottom: '10px',
        letterSpacing: '-0.6px',
        lineHeight: 1.2,
      }}>
        We'll be back soon
      </h1>

      {/* Subtitle */}
      <p className="maint-card" style={{
        color: 'rgba(255,255,255,0.5)',
        fontSize: '15px',
        maxWidth: '340px',
        lineHeight: '1.65',
        marginBottom: '36px',
      }}>
        Our team is performing scheduled maintenance on the M&J Enterprises portal.
        Hang tight — we'll be up shortly.
      </p>

      {/* Status pill */}
      <div className="maint-card" style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        background: 'rgba(245,158,11,0.08)',
        border: '1px solid rgba(245,158,11,0.25)',
        borderRadius: '100px',
        padding: '8px 20px',
        color: '#f59e0b',
        fontSize: '12px',
        fontWeight: '700',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        marginBottom: '44px',
      }}>
        <span style={{
          width: '7px', height: '7px',
          borderRadius: '50%',
          background: '#f59e0b',
          animation: 'pulse 1.6s ease-in-out infinite',
          display: 'inline-block',
          flexShrink: 0,
        }} />
        Maintenance in progress
      </div>

      {/* Try Again button */}
      <button
        className="maint-card try-again-btn"
        onClick={() => window.location.reload()}
        style={{
          background: 'linear-gradient(135deg, #f59e0b, #f97316)',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          padding: '13px 36px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(245,158,11,0.35)',
          letterSpacing: '0.3px',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
      >
        Try Again
      </button>

      {/* Footer */}
      <p className="maint-card" style={{
        color: 'rgba(255,255,255,0.18)',
        fontSize: '11px',
        marginTop: '52px',
        letterSpacing: '0.3px',
      }}>
        M&amp;J Enterprises &copy; {new Date().getFullYear()}
      </p>
    </div>
  );
}

export default ErrorBoundary;
